<template>
  <div class="validator-details">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        variant="text"
        class="mr-2"
        :to="{ name: 'validators' }"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4">{{ t('validators.details') }}</h1>
    </div>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
    ></v-skeleton-loader>
    
    <template v-else-if="validador">
      <!-- Status do Validador -->
      <v-card class="mb-4">
        <v-card-text class="d-flex align-center py-4">
          <v-avatar
            :color="validador.status === 'active' ? 'success' : 'grey'"
            class="mr-4"
            size="64"
          >
            <span class="text-h4 font-weight-bold text-white">{{ validador.name.charAt(0) }}</span>
          </v-avatar>
          
          <div>
            <div class="text-h5 font-weight-bold">
              {{ validador.name }}
            </div>
            <div class="text-caption">
              <span class="text-grey text-caption mr-2">Endereço:</span>
              <span class="text-caption">{{ validador.address }}</span>
              <v-btn icon size="x-small" class="ml-1" @click="copiarEndereco">
                <v-icon size="small">{{ enderecoCopiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              </v-btn>
            </div>
            <v-chip
              :color="validador.status === 'active' ? 'success' : 'grey'"
              size="small"
              class="mt-1"
            >
              {{ validador.status === 'active' ? 'Validador Ativo' : 'Validador Inativo' }}
            </v-chip>
          </div>
          
          <v-spacer></v-spacer>
          
          <div class="text-right">
            <div class="text-h6 font-weight-bold">
              {{ formatarValorToken(validador.stake) }}
            </div>
            <div class="text-caption">{{ t('validators.totalStake') }}</div>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Métricas do Validador -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="text-center py-4" height="100%">
            <div class="text-overline">{{ t('validators.meritScore') }}</div>
            <div class="d-flex justify-center my-2">
              <v-progress-circular
                :model-value="validador.merit"
                :color="getValidatorColor(validador.merit)"
                size="100"
                width="12"
              >
                {{ validador.merit }}
              </v-progress-circular>
            </div>
            <div class="text-caption mt-2">
              Baseado em contribuições para o ecossistema
            </div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4">
          <v-card class="text-center py-4" height="100%">
            <div class="text-overline">{{ t('validators.posemScore') }}</div>
            <div class="d-flex justify-center my-2">
              <v-progress-circular
                :model-value="validador.posemScore"
                color="primary"
                size="100"
                width="12"
              >
                {{ validador.posemScore }}
              </v-progress-circular>
            </div>
            <div class="text-caption mt-2">
              Combinação de Stake e Mérito
            </div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4">
          <v-card class="text-center py-4" height="100%">
            <div class="text-overline">{{ t('validators.leaderProb') }}</div>
            <div class="text-h3 my-2 font-weight-bold">
              {{ ((validador.stake * (1 + 0.1 * validador.merit)) / getTotalWeightedStake() * 100).toFixed(2) }}%
            </div>
            <div class="text-caption mt-2">
              Chance de ser selecionado como líder
            </div>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Detalhes e Performance -->
      <v-card class="my-4">
        <v-card-title>
          <v-icon color="primary" class="mr-2">mdi-chart-box-outline</v-icon>
          Performance do Validador
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <InfoItem label="Blocos Propostos" :valor="formatarNumeroGrande(validador.blocksProposed)" />
              <InfoItem :label="t('validators.uptime')" :valor="`${validador.uptime.toFixed(2)}%`" />
              <InfoItem label="Última Atividade" :valor="formatarData(validador.lastActive)" />
              <InfoItem :label="t('accounts.address')" :valor="validador.address" copiavel endereco />
            </v-col>
            
            <v-col cols="12" md="6">
              <InfoItem label="Época Atual" :valor="epocheAtual" />
              <InfoItem :label="t('blocks.epochPosition')" :valor="'--'" />
              <InfoItem label="Recompensas Acumuladas" :valor="formatarValorToken(0)" destacar />
              <InfoItem label="Tempo como Validador" :valor="'--'" />
            </v-col>
          </v-row>
          
          <!-- Gráfico de Desempenho -->
          <div class="mt-6">
            <h3 class="text-subtitle-1 mb-3">{{ t('validators.history') }}</h3>
            <div class="performance-graph pa-4 grey-darken-4">
              <div class="text-center mb-4 text-caption">
                Gráfico de performance dos últimos 30 dias (blocos produzidos, uptime, recompensas)
              </div>
              
              <!-- Simulação de Gráfico (em produção seria um gráfico real) -->
              <div class="graph-simulation">
                <div 
                  v-for="i in 30" 
                  :key="i" 
                  class="graph-bar"
                  :style="{
                    height: `${30}px`,
                    backgroundColor: getValidatorColor(validador.merit)
                  }"
                ></div>
                
                <div 
                  class="avg-line"
                  :style="{
                    bottom: '50px'
                  }"
                ></div>
              </div>
              
              <div class="d-flex justify-space-between mt-2">
                <span class="text-caption">30 dias atrás</span>
                <span class="text-caption">Hoje</span>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Blocos Propostos Recentemente -->
      <h2 class="text-h5 mb-3">{{ t('validators.recentBlocks') }}</h2>
      
      <v-card>
        <v-data-table
          :headers="cabecalhosBlocos"
          :items="blocosPropostos"
          hover
          density="comfortable"
          :items-per-page="5"
        >
          <!-- Bloco -->
          <template v-slot:item.number="{ item }">
            <router-link 
              :to="{ name: 'block-details', params: { id: item.number } }"
              class="text-primary text-decoration-none"
            >
              {{ item.number }}
            </router-link>
          </template>
          
          <!-- Timestamp -->
          <template v-slot:item.timestamp="{ item }">
            <div class="d-flex align-center">
              <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props">{{ tempoRelativo(item.timestamp) }}</span>
                </template>
                {{ formatarData(item.timestamp) }}
              </v-tooltip>
            </div>
          </template>
          
          <!-- Transações -->
          <template v-slot:item.transactionCount="{ item }">
            <v-chip
              size="small"
              :color="item.transactionCount > 0 ? 'primary' : 'grey'"
              variant="outlined"
              class="font-weight-medium"
            >
              {{ item.transactionCount }}
            </v-chip>
          </template>
          
          <!-- Tamanho -->
          <template v-slot:item.size="{ item }">
            {{ formatarBytes(item.size) }}
          </template>
          
          <!-- Recompensa -->
          <template v-slot:item.reward="{ item }">
            <span class="font-weight-medium">
              {{ formatarValorToken(item.reward) }}
            </span>
          </template>
          
          <!-- PoSEM Score -->
          <template v-slot:item.posemScore="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-progress-linear
                  v-bind="props"
                  :model-value="item.posemScore"
                  color="primary"
                  height="6"
                  rounded
                ></v-progress-linear>
              </template>
              {{ item.posemScore }}/100
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </template>
    
    <v-alert
      v-else
      type="error"
      class="mt-4"
    >
      Validador não encontrado. Verifique o endereço {{ id }} e tente novamente.
    </v-alert>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { formatarHash, formatarData, formatarBytes, formatarNumeroGrande, formatarValorToken, tempoRelativo } from '@/services/utilidades'
import InfoItem from '@/components/common/InfoItem.vue'

export default {
  name: 'ValidatorDetailsView',
  components: {
    InfoItem
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      carregando: true,
      validador: null,
      enderecoCopiado: false,
      epocheAtual: 0,
      cabecalhosBlocos: [
        { title: 'Bloco', key: 'number', align: 'start' },
        { title: 'Tempo', key: 'timestamp', align: 'start' },
        { title: 'Txs', key: 'transactionCount', align: 'center' },
        { title: 'Tamanho', key: 'size', align: 'end' },
        { title: 'Recompensa', key: 'reward', align: 'end' },
        { title: 'PoSEM Score', key: 'posemScore', align: 'center' }
      ],
      blocosPropostos: []
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    }
  },
  methods: {
    formatarHash,
    formatarData,
    formatarBytes,
    formatarNumeroGrande,
    formatarValorToken,
    tempoRelativo,
    
    getValidatorColor(merit) {
      // Gera cor baseada no mérito (vermelho para baixo, verde para alto)
      if (merit >= 90) return '#4CAF50' // Verde
      if (merit >= 70) return '#8BC34A' // Verde Claro
      if (merit >= 50) return '#FFC107' // Amarelo
      if (merit >= 30) return '#FF9800' // Laranja
      return '#F44336' // Vermelho
    },
    
    getTotalWeightedStake() {
      return this.blockchainStore.validators.reduce((sum, validator) => {
        return sum + validator.stake * (1 + 0.1 * validator.merit)
      }, 10000)
    },
    
    copiarEndereco() {
      navigator.clipboard.writeText(this.validador.address)
        .then(() => {
          this.enderecoCopiado = true
          setTimeout(() => {
            this.enderecoCopiado = false
          }, 2000)
        })
        .catch(err => {
          console.error('Erro ao copiar endereço:', err)
        })
    },
    
    async carregarValidador() {
      this.carregando = true
      
      try {
        // Buscar validador do store (carregado da API real do kernel)
        if (this.blockchainStore.validators.length === 0) {
          await this.blockchainStore.fetchValidators()
        }
        
        this.validador = this.blockchainStore.getValidatorByAddress(this.id)
        
        // Se não encontrou no cache, mostrar erro
        if (!this.validador) {
          console.error(`Validador ${this.id} não encontrado`)
          this.validador = null
          this.carregando = false
          return
        }
        
        // Carregar blocos propostos
        this.carregarBlocosPropostos()
        
      } catch (error) {
        console.error(`Erro ao carregar validador ${this.id}:`, error)
        this.validador = null
      } finally {
        this.carregando = false
      }
    },
    
    carregarBlocosPropostos() {
      // Dados de blocos propostos não disponíveis via API atual
      this.blocosPropostos = []
    }
  },
  async mounted() {
    await this.carregarValidador()
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.carregarValidador()
      }
    }
  }
}
</script>

<style scoped>
.validator-details {
  padding-bottom: 32px;
}

.performance-graph {
  border-radius: 8px;
  padding: 16px;
  position: relative;
}

.graph-simulation {
  display: flex;
  align-items: flex-end;
  height: 100px;
  position: relative;
  margin: 0 8px;
}

.graph-bar {
  flex: 1;
  margin: 0 1px;
  border-radius: 2px 2px 0 0;
  transition: height 0.3s ease;
}

.avg-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.5);
}

.avg-line::before {
  content: 'Média';
  position: absolute;
  right: -50px;
  top: -10px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}
</style>
